import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { AppContext } from "../../../context";
import { PillButton, TextButton } from "../../../components/Button";
import Tabs, { Tab } from "../../../shared-components/Tabs";
import { BRANDS } from "../../../configs";
import styles from "./ListView.scss";

const DestinationsView = ({
	region,
	selectedItem,
	itemToString,
	handleOnSelect
}) => {
	const [useAppState] = AppContext.useAppContext();
	return (
		<>
			<ul
				className={styles.popularChoice}
				aria-label={useAppState.getDictionary("most-popular-choice")}>
				{region?.popularDestinations?.map(destination => (
					<li key={destination.ProviderCode}>
						<PillButton
							brand={useAppState.brand}
							onClick={() => handleOnSelect(destination)}
							label={destination.Name}
						/>
					</li>
				))}
			</ul>
			<ul
				className={cx(styles.gatewayGroups, {
					[styles[useAppState.brand]]: styles[useAppState.brand]
				})}
				aria-label={useAppState.getDictionary("select-from-all-destinations")}>
				{region?.destinationGroups?.map(group => (
					<li key={`${group.ProviderCode}-${group.Name}`}>
						{group.Destinations ? (
							<>
								<TextButton
									type="button"
									className={cx(
										styles.destinationGroup,
										JSON.stringify(selectedItem) ===
											JSON.stringify(itemToString(group)) && styles.selected
									)}
									onClick={() => handleOnSelect(group)}
									label={group.Name}
								/>
								<ul className={styles.group}>
									{group.Destinations.map(destination => (
										<li key={destination.ProviderCode}>
											<TextButton
												type="button"
												className={cx(
													styles.gateway,
													JSON.stringify(selectedItem) ===
														JSON.stringify(itemToString(destination)) &&
														styles.selected
												)}
												onClick={() => handleOnSelect(destination)}
												label={destination.Name}
											/>
										</li>
									))}
								</ul>
							</>
						) : (
							<TextButton
								type="button"
								className={cx(
									styles.destinationGroup,
									JSON.stringify(selectedItem) ===
										JSON.stringify(itemToString(group)) && styles.selected
								)}
								onClick={() => handleOnSelect(group)}
								label={group.Name}
							/>
						)}
					</li>
				))}
			</ul>
		</>
	);
};

DestinationsView.propTypes = {
	region: PropTypes.shape({
		label: PropTypes.string,
		code: PropTypes.string,
		popularDestinations: PropTypes.arrayOf(
			PropTypes.shape({
				Name: PropTypes.string,
				Code: PropTypes.string,
				ProviderCode: PropTypes.string
			})
		),
		destinationGroups: PropTypes.arrayOf(
			PropTypes.shape({
				Destinations: PropTypes.arrayOf(
					PropTypes.shape({
						Name: PropTypes.string,
						Code: PropTypes.string,
						ProviderCode: PropTypes.string
					})
				),
				Name: PropTypes.string,
				Code: PropTypes.string,
				ProviderCode: PropTypes.string
			})
		)
	}).isRequired,
	selectedItem: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),
	itemToString: PropTypes.func.isRequired,
	handleOnSelect: PropTypes.func.isRequired
};

DestinationsView.defaultProps = {
	selectedItem: null
};

const ListView = ({ brand, data, selectedItem, onSelectItem }) => {
	const [useAppState] = AppContext.useAppContext();
	const itemToString = gateway => ({
		label: gateway.Name,
		value: gateway.ProviderCode
	});

	const handleOnSelect = gateway => {
		onSelectItem(itemToString(gateway));
	};

	return (
		<>
			<p
				className={cx(styles.heading, {
					[styles[useAppState.brand]]: styles[useAppState.brand]
				})}>
				{useAppState.getDictionary("where-can-we-take-you")}
			</p>
			<div
				className={cx(styles.container, {
					[styles[useAppState.brand]]: styles[useAppState.brand]
				})}
				body-scroll-lock-ignore="true">
				{data?.length > 1 ? (
					<Tabs
						activeKey={data[0]?.code}
						panelClassName={styles.tabPanel}
						className={cx(styles.tabs, { [styles[brand]]: styles[brand] })}
						tabClassName={styles.tab}
						activeClassName={styles.tabActive}>
						{data?.map((region, index) => (
							<Tab
								key={region?.code ?? index}
								eventKey={region?.code}
								title={region?.label}>
								<DestinationsView
									region={region}
									selectedItem={selectedItem}
									itemToString={itemToString}
									handleOnSelect={handleOnSelect}
								/>
							</Tab>
						))}
					</Tabs>
				) : (
					<div className={styles.scroll}>
						<DestinationsView
							region={data[0]}
							selectedItem={selectedItem}
							itemToString={itemToString}
							handleOnSelect={handleOnSelect}
						/>
					</div>
				)}
			</div>
		</>
	);
};

ListView.propTypes = {
	brand: PropTypes.oneOf(Object.keys(BRANDS)),
	data: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			code: PropTypes.string,
			popularDestinations: PropTypes.arrayOf(
				PropTypes.shape({
					Name: PropTypes.string,
					Code: PropTypes.string,
					ProviderCode: PropTypes.string
				})
			),
			destinationGroups: PropTypes.arrayOf(
				PropTypes.shape({
					Destinations: PropTypes.arrayOf(
						PropTypes.shape({
							Name: PropTypes.string,
							Code: PropTypes.string,
							ProviderCode: PropTypes.string
						})
					),
					Name: PropTypes.string,
					Code: PropTypes.string,
					ProviderCode: PropTypes.string
				})
			)
		})
	).isRequired,
	selectedItem: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),
	onSelectItem: PropTypes.func
};

ListView.defaultProps = {
	brand: BRANDS.SWG,
	selectedItem: null,
	onSelectItem: () => {}
};

export default ListView;
export { ListView };
